import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import artefactCategories from "../../data/artefacts";
import "./Artefacts.css";

const loadedArtefImgCache = new Map();

function ArtefCachedImg({
  src,
  alt = "",
  className = "artef__img",
  wrapperClass = "artef__img-wrapper",
  fit = "cover",
}) {
  const isCached = loadedArtefImgCache.get(src)?.status === "loaded";
  const [fadeIn, setFadeIn] = useState(isCached);
  const [showSkeleton, setShowSkeleton] = useState(!isCached);

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (document.getElementById("pli-shimmer-keyframes")) return;
    const styleEl = document.createElement("style");
    styleEl.id = "pli-shimmer-keyframes";
    styleEl.textContent = `
      @keyframes pli-shimmer { 100% { transform: translateX(100%); } }
      @keyframes pli-fade { from { opacity:0; } to { opacity:1; } }
    `;
    document.head.appendChild(styleEl);
  }, []);

  useEffect(() => {
    if (isCached) {
      setShowSkeleton(false);
      setFadeIn(true);
      return;
    }
    setFadeIn(false);
    setShowSkeleton(true);
    let cancelled = false;
    const img = new window.Image();
    img.src = src;
    img.onload = async () => {
      try {
        if (img.decode) await img.decode();
      } catch {}
      loadedArtefImgCache.set(src, { status: "loaded" });
      if (cancelled) return;
      setTimeout(() => {
        setShowSkeleton(false);
        setFadeIn(true);
      }, 500);
    };
    img.onerror = () => {
      loadedArtefImgCache.set(src, { status: "error" });
      setShowSkeleton(false);
      setFadeIn(true);
    };
    return () => {
      cancelled = true;
    };
  }, [src, isCached]);

  return (
    <div
      className={wrapperClass}
      style={{
        width: "100%",
        aspectRatio: "175/231",
        borderRadius: 12,
        overflow: "hidden",
        position: "relative",
        background: showSkeleton ? "#f2f2f2" : "transparent",
      }}
    >
      {showSkeleton && (
        <div
          className="artef__skeleton"
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            background: "#E0E0E0",
            zIndex: 2,
            borderRadius: 12,
            overflow: "hidden",
          }}
        >
          <div
            className="artef__shimmer"
            style={{
              position: "absolute",
              inset: 0,
              transform: "translateX(-100%)",
              background:
                "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,.66) 50%, rgba(255,255,255,0) 100%)",
              animation: "pli-shimmer 1.2s infinite",
            }}
          />
        </div>
      )}
      <img
        src={src}
        alt={alt}
        className={className}
        style={{
          objectFit: fit,
          opacity: fadeIn ? 1 : 0,
          transition: "opacity 0.45s cubic-bezier(.42,0,.58,1.0)",
          width: "100%",
          height: "100%",
          display: "block",
          borderRadius: 12,
          zIndex: 1,
          background: "transparent",
        }}
        loading="eager"
        decoding="async"
      />
    </div>
  );
}

const Artefacts = () => {
  return (
    <div style={{ margin: "0 0 83.5px 0" }}>
      <div style={{ fontSize: "12px", margin: "0px 15px", display: "flex" }}>
        <svg
          style={{ marginRight: "5px" }}
          width="15"
          height="15"
          viewBox="0 0 15 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M7.5 0C6.88 0 6.24834 0.119256 5.755 0.365292L0.46833 2.99747C0.15083 3.17095 0 3.45119 0 3.7531V9.95655C0 10.9582 0.690005 12.0633 1.58834 12.5103L5.96334 14.6738C6.83834 15.1083 8.16166 15.1092 9.03666 14.6738L13.4117 12.5103C14.3175 12.06 15 10.9666 15 9.95655V3.7531C15 3.45119 14.8475 3.14593 14.5317 2.99747L9.24499 0.365292C8.75166 0.119256 8.12 0 7.5 0ZM6.51 1.85069C7.03 1.59214 7.97 1.59214 8.49 1.85069L12.2917 3.7531L8.49 5.62965C7.97 5.8882 7.03 5.8882 6.51 5.62965C6.45166 5.60046 3.26167 4.02833 2.70833 3.7531L6.51 1.85069ZM1.66667 5.10839C2.435 5.49121 3.0775 5.79144 3.90667 6.20345C4.59584 6.5454 5.51917 7.0058 5.755 7.11505C6.11667 7.28269 6.35583 7.36693 6.66667 7.42781V13.1617L2.3175 10.9991C1.98584 10.8339 1.66667 10.3227 1.66667 9.95655V5.10839ZM13.3333 5.10839V9.95655C13.3333 10.331 12.9958 10.8556 12.6567 11.0249L8.33333 13.1617V7.42781C8.64417 7.36693 8.96166 7.26935 9.21833 7.14091L13.3333 5.10839Z"
            fill="black"
          />
        </svg>
        <p>Артефакты</p>
      </div>

      {artefactCategories.map((category, index) => (
        <div key={index}>
          <div
            style={{ display: "flex", alignItems: "center", margin: "15px" }}
          >
            <h2 style={{ fontWeight: "500", fontSize: "18px" }}>
              {category.title}
            </h2>
            <div
              style={{
                flex: 1,
                height: "1px",
                background: "#000",
                marginLeft: "10px",
                opacity: "0.1",
              }}
            ></div>
          </div>

          <div className="artef__wrapper">
            {category.items.map((item, idx) => (
              <Link
                to={`/artefacts/${item.link}`}
                state={item}
                className="artef__item"
                key={idx}
              >
                <svg
                  className="shop__svg"
                  width="25"
                  height="25"
                  viewBox="0 0 25 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="12.5"
                    width="17.6777"
                    height="17.6777"
                    rx="8.83883"
                    transform="rotate(45 12.5 0)"
                    fill="black"
                    fillOpacity="0.4"
                  />
                  <path
                    d="M11.5423 9.62565L15.3757 9.62565M15.3757 9.62565L15.3757 13.459M15.3757 9.62565L9.62565 15.3757"
                    stroke="white"
                    strokeWidth="0.9"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <ArtefCachedImg src={item.image} alt={item.name} />
                <div className="artef__text">
                  <div style={{ fontSize: "12px" }}>{item.name}</div>
                  <div
                    style={{
                      fontSize: "9px",
                      opacity: "0.6",
                      margin: "3px 0 6px",
                    }}
                  >
                    {item.description}
                  </div>
                  <div
                    style={{
                      fontSize: "9px",
                      backgroundColor: "white",
                      color: "black",
                      padding: "5px",
                      textAlign: "center",
                      borderRadius: "8px",
                    }}
                  >
                    Подробнее
                  </div>
                </div>
                <div
                  className="shop__img-overlay"
                  style={{ borderRadius: "8px" }}
                />
              </Link>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default Artefacts;
